export class Util {
  static version: string = '0.2.90';

  static isEmail(str: string): boolean {
    if(!str)
      return false;
    return str.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi) != null
  }

  static isBase64Image(data: string): boolean {
    return data != null && data.match(/^data:image\/(jpeg|jpg|png|gif);base64,/).length > 0;
  }

  static getName(user: any): string {
    if(!user)
      return;
    if(user.firstName && user.lastName)
      return user.firstName + ' ' + user.lastName;
    if(user.firstName)
      return user.firstName;
    if(user.lastName)
      return user.lastName;
    return user.email;
  }

  static sanitizeIdentifier(familyIdentifier : string | undefined, identifier : string | undefined) : string {
    let ret : string = '';
    if (familyIdentifier && identifier) {
      ret = familyIdentifier;
      const idx : number = ret.lastIndexOf('-');

      if (idx > -1) {
        ret = ret.substring(0, idx);
      }
      if (identifier.length > familyIdentifier.length) {
        ret = ret + identifier.substring(familyIdentifier.length);
      }

    }
    return ret;
  }

  static sanitizePatientIdentifier(identifier : string) : string{
    //223-03-D-20160-KIN-FE-1984
    let ret: string = identifier;
    let idx : number = identifier.indexOf('-');
    if (idx > -1) {
      idx = identifier.indexOf('-', idx+1);
      if (idx > -1) {
        idx = identifier.indexOf('-', idx+1);
        if (idx > -1) {
          idx = identifier.indexOf('-', idx+1);
          if (idx > -1) {
            let familyIdentifier : string = identifier.substring(0, idx);
            ret = this.sanitizeIdentifier(familyIdentifier, identifier);
          }
        }
      }
    }
    return ret;
  }

  //finds the index that element would have in arr if it were added to arr
  static getSortedIndex<T>(arr: T[], elem: T, comparator: (a: T, b: T) => number): number {
    if(arr.length == 0)
      return 0;
    let left: number = 0, right: number = arr.length - 1, index: number;
    while(true) {
      index = Math.floor((left + right) / 2);
      if(comparator(elem, arr[index]) < 0) {
        if(index == 0)
          return 0;
        if(right == left || index == left)
          return index;
        right = index - 1;
      } else {
        if(index == arr.length - 1)
          return arr.length;
        if(right == left || index == right)
          return index + 1;
        left = index + 1;
      }
    }
  }

  //finds an index in arr where the corresponding value is equivalent (comparator return 0) to elem
  //if it finds a match - returns the index of the match (not necessarily the first index)
  //if it doesn't find a match - returns a negative number represending the index where the new element should be added
    //returns -1 => should add at index 0, -2 => 1, -3 => 2, ...
  static sortedIndexOf<T>(arr: T[], elem: T, comparator: (a: T, b: T) => number): number {
    if(arr.length == 0)
      return -1;
    let left: number = 0, right: number = arr.length - 1, index: number;
    while(left <= right) {
      index = Math.floor((left + right) / 2);
      const comparrison: number = comparator(elem, arr[index]);
      if(comparrison < 0)
        right = index - 1;
      else if(comparrison > 0)
        left = index + 1
      else
        return index;
    }
    return -1 - left;
  }

  static arraysEqual(a: any[], b: any[], comparator: (a: any, b: any) => boolean = (a: any, b: any): boolean => a === b): boolean {
    if(a == null || b == null)
      return a == b;
    if(a.length != b.length)
      return false;
    return a.findIndex((value: any, index: number): boolean => !comparator(b[index], value)) == -1;
  }
}

export class ErrorField<T> {
  _value: T;
  validator: (value: T, self: ErrorField<T>) => string;
  error: string;
  showError: boolean;

  set value(value: T) {
    this._value = value;
    this.error = this.validator(value, this);
  }

  get value() {
    return this._value;
  }

  constructor(validator: (value: T, self: ErrorField<T>) => string, value: T = null, showError: boolean = false) {
    this.validator = validator;
    this.showError = showError;
    this.value = value;
  }

  isValid(): boolean {
    return this.error == null;
  }

  showingError(): boolean {
    return this.showError && !this.isValid();
  }
}

export enum PromiseStatus {
  PENDING,
  RESOLVED,
  REJECTED
}

export class SelfAwarePromise<T = any> implements PromiseLike<T> {
  private status: PromiseStatus;
  private promise: Promise<T>;

  constructor(executor: (resolve: (value?: T | PromiseLike<T>) => void, reject: (reason?: any) => void) => void) {
    this.status = PromiseStatus.PENDING;
    this.promise = new Promise<T>(executor);
    this.promise.then((value: T): T => {
      this.status = PromiseStatus.RESOLVED;
      return value;
    }, (reason: any) => {
      this.status = PromiseStatus.REJECTED;
      throw reason;
    });
  }

  then<TResult1, TResult2>(onfulfilled?: (value: T) => TResult1 | PromiseLike<TResult1>, onrejected?: (reason: any) => TResult2 | PromiseLike<TResult2>): Promise<TResult1 | TResult2> {
    return this.promise.then(onfulfilled, onrejected);
  }

  catch<TResult>(onrejected?: (reason: any) => TResult | PromiseLike<TResult>): Promise<T | TResult> {
    return this.promise.catch(onrejected);
  }

  finally(onfinally?: () => void): Promise<T> {
    return this.promise.finally(onfinally);
  }

  getStatus(): PromiseStatus {
    return this.status;
  }

  isPending(): boolean {
    return this.status === PromiseStatus.PENDING;
  }

  isResolved(): boolean {
    return this.status === PromiseStatus.RESOLVED;
  }

  isRejected(): boolean {
    return this.status === PromiseStatus.REJECTED;
  }

  getPromise(): Promise<T> {
    return this.promise;
  }

  static fromPromise<T>(promise: Promise<T>) {
    return new SelfAwarePromise<T>((resolve, reject) => {
      promise.then(resolve).catch(reject);
    });
  }
}
